// components/Layout/Sidebar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatTime, parseSRTTimeToSeconds } from "../../utils/formatTime";
import { getPortalContainer } from "../../utils/portalContainer";
import { safeGetItem, safeSetItem } from "../../utils/safeStorage";
import { useVideo } from "../../context/VideoContext";
import { useSelectedSubtitles } from "../../context/SelectedSubtitlesContext";
import { useSubtitleSettings } from "../../context/SubtitleSettingsContext";
import { useNotification } from "../../context/NotificationContext";
import { usePackagesModal } from "../../context/PackagesModalContext";
import { savePhrase } from "../../services/userSaveWordApi";
import {
  searchCompletedSubtitles,
  submitVoteRequest,
} from "../../services/learningApi";
import { fetchSubtitleContent } from "../../services/subtitleApi";
import useSubtitleManager from "../../hooks/useSubtitleManager";
import { checkUserPremium } from "../../utils/handleJWT";
import "./Sidebar.css";

const OVERSCAN = 5;
const ITEM_MARGIN = 3; // معادل margin-bottom زیرنویس
const ESTIMATED_HEIGHT = 26;
const AUTO_SCROLL_RESUME_DELAY = 4000; // بعد از پایان تعامل دستی، اتواسکرول دوباره فعال می‌شود

const findFirstVisible = (positions, offset) => {
  let lo = 0;
  let hi = positions.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (positions[mid].top <= offset) lo = mid;
    else hi = mid - 1;
  }
  return lo;
};

const findLastVisible = (positions, offset) => {
  let lo = 0;
  let hi = positions.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (positions[mid].top + positions[mid].height >= offset) hi = mid;
    else lo = mid + 1;
  }
  return lo;
};

const SubtitleTab = ({
  cues,
  currentTime = 0,
  onSeek,
  fontScale = 1,
  onOpenDetail,
}) => {
  const listRef = useRef(null);
  const observerRef = useRef(null);
  const rafRef = useRef(null);
  const heightsRef = useRef([]);
  const pointerInsideRef = useRef(false);
  const lastInteractionRef = useRef(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [geoVersion, setGeoVersion] = useState(0);

  const { autoLoadedSub1Id, learningData, learningSubOneID } = useVideo();
  const { getActiveSubtitle } = useSelectedSubtitles();

  // آی‌دی زیرنویس اول فعلی
  const subtitle1 = getActiveSubtitle(1);
  const subOneID =
    subtitle1?.subtitlesUploadId ||
    subtitle1?.onlineId ||
    subtitle1?._id ||
    autoLoadedSub1Id ||
    null;

  // نگاشت subtitleIndex زیرنویس به وضعیت اصطلاحات (کامل/ناقص)؛
  // اگر یک دیالوگ هم اصطلاح کامل داشته باشد و هم ناقص، رنگ کامل اولویت دارد.
  const statusByCueKey = useMemo(() => {
    const map = new Map();
    if (!learningData || learningSubOneID !== subOneID) return map;
    for (const item of learningData.items || []) {
      const key = Number(item.subtitleIndex);
      if (!Number.isFinite(key)) continue;
      const complete = !!(item.cefr && item.cefr !== "Unknown");
      if (complete) {
        map.set(key, "complete");
      } else if (!map.has(key)) {
        map.set(key, "incomplete");
      }
    }
    return map;
  }, [learningData, learningSubOneID, subOneID]);

  // نگاشت subtitleIndex به آیتم‌های لغت/اصطلاح آن دیالوگ (برای دکمه‌های current-subtitle)
  const itemsByCueKey = useMemo(() => {
    const map = new Map();
    if (!learningData || learningSubOneID !== subOneID) return map;
    for (const item of learningData.items || []) {
      const key = Number(item.subtitleIndex);
      if (!Number.isFinite(key)) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }
    return map;
  }, [learningData, learningSubOneID, subOneID]);

  const activeIndex = useMemo(() => {
    if (!cues || cues.length === 0) return -1;
    return cues.findIndex((cue) => {
      const start = parseSRTTimeToSeconds(cue.startTime);
      const end = parseSRTTimeToSeconds(cue.endTime);
      return currentTime >= start && currentTime <= end;
    });
  }, [cues, currentTime]);

  // فقط زیرنویس‌های در حال نمایش + بافر قبل و بعد در DOM قرار می‌گیرند؛
  // ارتفاع آیتم‌ها با ResizeObserver اندازه‌گیری و در کش ذخیره می‌شود.
  const geometry = useMemo(() => {
    const n = cues?.length || 0;
    const heights = heightsRef.current;
    const arr = new Array(n);
    let top = 0;
    for (let i = 0; i < n; i++) {
      const h = heights[i] || ESTIMATED_HEIGHT;
      arr[i] = { top, height: h };
      top += h + ITEM_MARGIN;
    }
    return { positions: arr, total: n > 0 ? top : 0 };
  }, [cues, geoVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    heightsRef.current = [];
    setGeoVersion((v) => v + 1);
    setScrollTop(0);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [cues]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const update = () => setViewportHeight(el.clientHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [cues]);

  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      let changed = false;
      for (const entry of entries) {
        const idx = Number(entry.target.dataset.index);
        const bbs = entry.borderBoxSize;
        const h =
          (Array.isArray(bbs) && bbs[0] ? bbs[0].blockSize : entry.contentRect.height) || 0;
        if (h > 0 && Math.abs((heightsRef.current[idx] || 0) - h) > 1) {
          heightsRef.current[idx] = h;
          changed = true;
        }
      }
      if (changed) setGeoVersion((v) => v + 1);
    });
    observerRef.current = ro;
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handleScroll = useMemo(
    () => () => {
      lastInteractionRef.current = Date.now();
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setScrollTop(listRef.current?.scrollTop || 0);
      });
    },
    []
  );

  // زیرنویس فعال همیشه وسط دیده شود؛ اما وقتی کاربر دستی در حال
  // اسکرول/لمس است یا موس روی سایدبار است، اتواسکرول موقتاً غیرفعال می‌شود
  // تا عقب و جلو نپرد. بعد از چند ثانیه بدون تعامل، دوباره فعال می‌شود.
  useEffect(() => {
    if (activeIndex < 0) return;
    if (pointerInsideRef.current) return;
    if (Date.now() - lastInteractionRef.current < AUTO_SCROLL_RESUME_DELAY) return;
    const el = listRef.current;
    const pos = geometry.positions[activeIndex];
    if (!el || !pos) return;
    const target = Math.max(0, pos.top - el.clientHeight / 2 + pos.height / 2);
    if (Math.abs(el.scrollTop - target) > 4) {
      el.scrollTop = target;
      setScrollTop(target);
    }
  }, [activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cues || cues.length === 0) {
    return (
      <div className="tab-placeholder">
        <i className="fas fa-closed-captioning"></i>
        <p>زیرنویس فعالی انتخاب نشده است</p>
      </div>
    );
  }

  const { positions, total } = geometry;
  const n = cues.length;

  let start = 0;
  let end = Math.min(n - 1, OVERSCAN);
  if (viewportHeight > 0) {
    start = Math.max(0, findFirstVisible(positions, scrollTop) - OVERSCAN);
    end = Math.min(
      n - 1,
      findLastVisible(positions, scrollTop + viewportHeight) + OVERSCAN
    );
  }

  const rows = [];
  for (let i = start; i <= end; i++) {
    const cue = cues[i];
    const cueKey = Number(cue.id);
    const status =
      statusByCueKey.get(Number.isFinite(cueKey) ? cueKey : i) || "";
    rows.push({
      index: i,
      cue,
      start: parseSRTTimeToSeconds(cue.startTime),
      top: positions[i].top,
      status,
    });
  }

  const activeCue = activeIndex >= 0 ? cues[activeIndex] : null;

  // لغت/اصطلاح‌های متعلق به دیالوگ در حال پخش
  const activeCueKey = activeCue ? Number(activeCue.id) : NaN;
  const activeItems = Number.isFinite(activeCueKey)
    ? itemsByCueKey.get(activeCueKey) || []
    : [];
  const activeStatus = activeItems.some(
    (item) => item.cefr && item.cefr !== "Unknown"
  )
    ? "complete"
    : activeItems.length > 0
    ? "incomplete"
    : "";

  return (
    <div
      className="subtitle-sidebar"
      style={{ "--sidebar-font-scale": String(fontScale) }}
      onPointerEnter={() => {
        pointerInsideRef.current = true;
        lastInteractionRef.current = Date.now();
      }}
      onPointerLeave={() => {
        pointerInsideRef.current = false;
        lastInteractionRef.current = Date.now();
      }}
    >
      {activeCue && (
        <div
          className={`current-subtitle ${
            activeStatus ? `word-item-${activeStatus}` : ""
          }`}
        >
          <div className="current-subtitle-top">
            <span className="current-subtitle-label">
              <i className="fas fa-play"></i>
              در حال پخش
            </span>

            {activeItems.length > 0 && (
              <div className="current-subtitle-words">
                {activeItems.map((item, index) => (
                  <button
                    key={`${item.subtitleIndex}-${index}`}
                    type="button"
                    className="current-subtitle-word-btn"
                    onClick={() => onOpenDetail(item)}
                    title="مشاهده توضیحات بیشتر"
                  >
                    {item.category}
                  </button>
                ))}
              </div>
            )}
          </div>

          <p className="current-subtitle-text">{activeCue.text}</p>
        </div>
      )}

      <div
        className="subtitle-list"
        ref={listRef}
        onScroll={handleScroll}
        onTouchStart={() => {
          lastInteractionRef.current = Date.now();
        }}
      >
        <div
          style={{ position: "relative", width: "100%", height: total, flexShrink: 0 }}
        >
          {rows.map(({ index, cue, start, top, status }) => (
            <button
              key={index}
              data-index={index}
              ref={(el) => {
                if (el) observerRef.current?.observe(el);
              }}
              className={`subtitle-item ${index === activeIndex ? "active" : ""} ${
                status ? `word-item-${status}` : ""
              }`}
              style={{ position: "absolute", top, left: 0, right: 0 }}
              onClick={() => onSeek?.(start)}
              title="پرش به این بخش"
            >
              <span className="subtitle-time">{formatTime(start)}</span>
              <span className="subtitle-text">{cue.text}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const LearningItemRow = ({ item, onPlay, onOpenDetail }) => {
  const { learningData } = useVideo();
  const hasFullAccess = checkUserPremium() || !!learningData?.betaAccess;
  const maskedPhrase = "فقط برای کاربران ویژه";
  const phrase =
    hasFullAccess && item.phrase ? item.phrase : hasFullAccess ? "—" : maskedPhrase;
  const meaning =
    hasFullAccess && item.meaning ? item.meaning : hasFullAccess ? "—" : maskedPhrase;

  const isComplete = item.cefr && item.cefr !== "Unknown";

  return (
    <button
      type="button"
      className={`word-item ${
        isComplete ? "word-item-complete" : "word-item-incomplete"
      }`}
      onClick={() => onOpenDetail(item)}
      title="مشاهده توضیحات بیشتر"
    >
      <div className="word-item-main">
        <span className="word-item-phrase">{phrase}</span>
        <span className="word-item-category">
          {item.category}
          {item.cefr && item.cefr !== "Unknown" ? ` · ${item.cefr}` : ""}
        </span>
      </div>

      <p className="word-item-meaning">{meaning}</p>

      <p className="word-item-sentence">
        <i className="fas fa-quote-right"></i> {item.sentence}
      </p>

      <div className="word-item-actions">
        <button
          type="button"
          className="word-item-btn word-item-play"
          onClick={(event) => {
            event.stopPropagation();
            onPlay(item);
          }}
          disabled={typeof item.timestamp !== "number"}
          title="پخش از این بخش"
        >
          <i className="fas fa-play"></i>
          <span>پخش</span>
        </button>

        <button
          type="button"
          className="word-item-btn"
          onClick={(event) => {
            event.stopPropagation();
            onOpenDetail(item);
          }}
          title="توضیحات بیشتر"
        >
          <i className="fas fa-info-circle"></i>
          <span>توضیحات بیشتر</span>
        </button>
      </div>
    </button>
  );
};

const CATEGORY_PERSIAN = {
  phrasal_verb: "فعل عبارتی",
  expression: "اصطلاح / عبارت",
  idiom: "اصطلاح تمثیلی/کنایه‌ای",
  word: "کلمه",
  collocation: "هم‌آیند / ترکیب واژگانی",
  phrase: "عبارت",
  proverb: "ضرب‌المثل",
  slang: "عامیانه / زبان محاوره",
  phrasal_prepositional_verb: "فعل عبارتی حرف‌اضافه‌ای",
  prepositional_phrase: "عبارت حرف‌اضافه‌ای",
  noun_phrase: "عبارت اسمی",
  verb_phrase: "عبارت فعلی",
  adjective_phrase: "عبارت وصفی",
  adverb_phrase: "عبارت قیدی",
  phrasal_adjective: "صفت عبارتی",
  compound_word: "کلمهٔ مرکب",
  interjection: "حرف ندا / اصطلاح تعجبی",
  conjunction: "حرف ربط",
  connector: "عبارت ربطی / پیونددهنده",
  fixed_expression: "عبارت ثابت",
  set_phrase: "عبارت قالبی",
  catchphrase: "جملهٔ مشهور / شعار",
  cliché: "کلیشه",
  euphemism: "حسن‌تعابیر / واژهٔ جایگزین مؤدبانه",
  metaphor: "استعاره",
  simile: "تشبیه",
  hyperbole: "اغراق",
  colloquialism: "گفتاری / محاوره‌ای",
  abbreviation: "مخفف",
  acronym: "سرواژه",
  initialism: "سرواژهٔ حرف‌به‌حرف",
  loanword: "وام‌واژه",
};

const POS_PERSIAN = {
  noun: "اسم",
  verb: "فعل",
  adjective: "صفت",
  adverb: "قید",
  pronoun: "ضمیر",
  preposition: "حرف اضافه",
  conjunction: "حرف ربط",
  interjection: "حرف ندا",
  determiner: "معیّن‌کننده / حرف تعریف",
  article: "حرف تعریف",
  numeral: "عدد / عددی",
  particle: "حرف اضافه‌نما / ذره",
  auxiliary_verb: "فعل کمکی",
  modal_verb: "فعل وجهی",
  gerund: "اسم مصدر / فعل‌اسم",
  infinitive: "مصدر",
  participle: "وجه وصفی / اسم‌فعل",
  phrasal_verb: "فعل عبارتی",
  verb_phrase: "عبارت فعلی",
  noun_phrase: "عبارت اسمی",
  adjective_phrase: "عبارت وصفی",
  adverb_phrase: "عبارت قیدی",
  prepositional_phrase: "عبارت حرف‌اضافه‌ای",
  expression: "اصطلاح / عبارت",
  idiom: "اصطلاح تمثیلی/کنایه‌ای",
  phrase: "عبارت",
  collocation: "هم‌آیند / ترکیب واژگانی",
  word: "کلمه",
};

const normalizeKey = (value) =>
  (value || "").trim().toLowerCase().replace(/\s+/g, "_");

const translateLabel = (value, map) => map[normalizeKey(value)] || "";

export const WordsDetailBox = ({ item, onClose, fontScale = 1 }) => {
  const { learningData, videoMetadata } = useVideo();
  const isPremium = checkUserPremium();
  const hasFullAccess = isPremium || !!learningData?.betaAccess;
  const maskedPhrase = "فقط برای کاربران ویژه";
  const { showSuccess, showError } = useNotification();
  const { showPackages } = usePackagesModal();
  const { settings } = useSubtitleSettings();
  const fromLang = settings.subtitle1?.language || "en";
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const categoryFa = translateLabel(item.category, CATEGORY_PERSIAN);
  const posFa = translateLabel(item.partOfSpeech, POS_PERSIAN);
  const hasLevel = !!item.cefr && item.cefr !== "Unknown";

  const renderValue = (en, fa) => (
    <span className="word-detail-value">
      <span dir="ltr">{en}</span>
      {fa && <span className="word-detail-fa">({fa})</span>}
    </span>
  );

  const handleSave = async () => {
    if (!isPremium) {
      showPackages({ onSelect: () => {} });
      showError("برای ذخیره اصطلاح نیاز به اشتراک ویژه دارید", {
        duration: 4000,
        position: "top-center",
      });
      return;
    }

    const phrase = item.phrase || "";
    try {
      setSaving(true);

      const response = await savePhrase({
        fromLang,
        toLang: "fa",
        phrase,
        meaning: item.meaning || "",
        category: item.category || "",
        sentence: item.sentence || "",
        sentenceAiTranslate: item.sentenceAiTranslate || "",
        timestamp:
          typeof item.timestamp === "number" && !isNaN(item.timestamp)
            ? item.timestamp
            : undefined,
        movieTitle: videoMetadata?.filmName || "",
        season: videoMetadata?.season || "",
        episode: videoMetadata?.episode || "",
        cefr:
          item.cefr && item.cefr !== "Unknown" ? item.cefr : "",
        partOfSpeech: item.partOfSpeech || "",
        explanation: item.explanation || item.briefExplanation || "",
        examples: Array.isArray(item.examples) ? item.examples : [],
        synonyms: Array.isArray(item.synonyms) ? item.synonyms : [],
        antonyms: Array.isArray(item.antonyms) ? item.antonyms : [],
      });

      if (response?.success) {
        setSaved(true);
        showSuccess(`اصطلاح "${phrase}" با موفقیت ذخیره شد`, {
          duration: 3000,
          position: "top-center",
        });
      } else {
        if (response?.status !== 402) {
          showError(response?.error || "خطا در ذخیره اصطلاح", {
            duration: 4000,
            position: "top-center",
          });
        }
      }
    } catch (error) {
      console.error("خطا در ذخیره اصطلاح:", error);

      if (error.response?.status === 402) {
        showPackages({ onSelect: () => {} });
      } else {
        showError("خطا در ارتباط با سرور", {
          duration: 4000,
          position: "top-center",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="word-detail-overlay" onClick={onClose}>
      <div
        className="word-detail-box"
        style={{ "--word-detail-font-scale": String(fontScale) }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="word-detail-header">
          <span className="word-detail-title">
            <i className="fas fa-book-open"></i>
            توضیحات
          </span>
          <div className="word-detail-header-actions">
            <button
              type="button"
              className="word-detail-save"
              onClick={handleSave}
              disabled={saving || saved}
              title={saved ? "ذخیره شد" : "ذخیره اصطلاح"}
            >
              {saving ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : saved ? (
                <i className="fas fa-check"></i>
              ) : (
                <i className="fas fa-floppy-disk"></i>
              )}
              <span>{saving ? "..." : saved ? "ذخیره شد" : "ذخیره"}</span>
            </button>
            <button
              type="button"
              className="word-detail-close"
              onClick={onClose}
              title="بستن"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div className="word-detail-body">
          {hasFullAccess ? (
            <>
              <div className="word-detail-row">
                <span className="word-detail-label">phrase (عبارت اصلی)</span>
                <span className="word-detail-value" dir="ltr">
                  {item.phrase || "—"}
                </span>
              </div>
              {hasLevel && (
                <div className="word-detail-row">
                  <span className="word-detail-label">سطح</span>
                  <span className="word-detail-value" dir="ltr">
                    {item.cefr}
                  </span>
                </div>
              )}
              {item.category && (
                <div className="word-detail-row">
                  <span className="word-detail-label">category (نوع)</span>
                  {renderValue(item.category, categoryFa)}
                </div>
              )}
              {item.meaning && (
                <div className="word-detail-row">
                  <span className="word-detail-label">meaning (معنی)</span>
                  <span className="word-detail-value">{item.meaning}</span>
                </div>
              )}
              {item.explanation && (
                <div className="word-detail-row">
                  <span className="word-detail-label">explanation</span>
                  <span className="word-detail-value" dir="ltr">
                    {item.explanation}
                  </span>
                </div>
              )}
              {item.partOfSpeech && (
                <div className="word-detail-row">
                  <span className="word-detail-label">
                    partOfSpeech (نقش دستوری)
                  </span>
                  {renderValue(item.partOfSpeech, posFa)}
                </div>
              )}
              {item.sentence && (
                <div className="word-detail-row">
                  <span className="word-detail-label">
                    {item.sentenceAiTranslate
                      ? "جمله و ترجمه هوش مصنوعی"
                      : "جمله"}
                  </span>
                  <div className="word-detail-list">
                    {item.sentence && (
                      <span className="word-detail-list-item" dir="ltr">
                        <i className="fas fa-quote-right"></i> {item.sentence}
                      </span>
                    )}
                    {item.sentenceAiTranslate && (
                      <span className="word-detail-list-item">
                        <i className="fas fa-language"></i>{" "}
                        {item.sentenceAiTranslate}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {!item.explanation && item.briefExplanation && (
                <div className="word-detail-row">
                  <span className="word-detail-label">توضیح مختصر</span>
                  <span className="word-detail-value" dir="ltr">
                    {item.briefExplanation}
                  </span>
                </div>
              )}
              {item.examples?.length > 0 && (
                <div className="word-detail-row">
                  <span className="word-detail-label">examples (مثال‌ها)</span>
                  <div className="word-detail-list">
                    {item.examples.map((example, index) => (
                      <span className="word-detail-list-item" dir="ltr" key={index}>
                        {example}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {item.synonyms?.length > 0 && (
                <div className="word-detail-row">
                  <span className="word-detail-label">
                    synonyms (مترادف‌ها)
                  </span>
                  <div className="word-detail-tags">
                    {item.synonyms.map((syn, index) => (
                      <span className="word-detail-tag" dir="ltr" key={index}>
                        {syn}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {item.antonyms?.length > 0 && (
                <div className="word-detail-row">
                  <span className="word-detail-label">antonyms (متضادها)</span>
                  <div className="word-detail-tags">
                    {item.antonyms.map((ant, index) => (
                      <span className="word-detail-tag" dir="ltr" key={index}>
                        {ant}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="word-detail-locked">
              {maskedPhrase} — این اطلاعات مخصوص کاربران ویژه است.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const WordsBetaBadge = () => (
  <div className="words-beta-badge">🧪 نسخه آزمایشی</div>
);

const WordsTab = ({ fontScale = 1, showCompleteOnly = true, onOpenDetail }) => {
  const {
    autoLoadedSub1Id,
    videoRef,
    handleSeek,
    learningData,
    learningSubOneID,
    isLearningLoading,
    learningError,
    learningErrorSubOneID,
    fetchUserLearning,
    videoMetadata,
    setSubtitle1Text,
    setSubtitle1Loaded,
    setSubtitle1Delay,
    setAutoLoadedSub1Id,
    removeSubtitle,
    savedOriginalSubtitle,
    setSavedOriginalSubtitle,
    showCompatPrompt,
    setShowCompatPrompt,
    pickAnotherMode,
    setPickAnotherMode,
    isSwapping,
    setIsSwapping,
    learningSearchResults: searchResults,
    setLearningSearchResults: setSearchResults,
  } = useVideo();
  const { getActiveSubtitle, currentCollection } = useSelectedSubtitles();
  const { showSuccess, showError } = useNotification();
  const { setupSubtitle } = useSubtitleManager(videoRef);

  // آی‌دی زیرنویس اول (subOneID) از چند مسیر احتمالی
  const subtitle1 = getActiveSubtitle(1);
  const subOneID =
    subtitle1?.subtitlesUploadId ||
    subtitle1?.onlineId ||
    subtitle1?._id ||
    autoLoadedSub1Id ||
    null;

  // داده‌های یادگیری فقط وقتی معتبرند که برای همین زیرنویس اول دریافت شده‌باشند؛
  // با تغییر فیلم یا زیرنویس اول (تغییر subOneID) داده قبلی دیگر نمایش داده نمی‌شود.
  const hasCachedData = !!learningData && learningSubOneID === subOneID;
  const showLearningError = !!learningError && learningErrorSubOneID === subOneID;

  // مرتب‌سازی بر اساس subtitleIndex صعودی (ترتیب وقوع در فیلم)
  // اگر "اصطلاحات با توضیحات کامل" فعال باشد، فقط آیتم‌هایی که سطح مشخصی
  // دارند (cefr !== "Unknown") نمایش داده می‌شوند.
  const sortedItems = useMemo(() => {
    if (!hasCachedData) return [];
    const items = showCompleteOnly
      ? learningData.items.filter(
          (item) => item.cefr && item.cefr !== "Unknown"
        )
      : learningData.items;
    return [...items].sort(
      (a, b) => (a.subtitleIndex ?? 0) - (b.subtitleIndex ?? 0),
    );
  }, [hasCachedData, learningData, showCompleteOnly]);

  const handleCheckLearning = () => {
    fetchUserLearning(subOneID);
  };

  // ثبت درخواست استخراج لغات و اصطلاحات (رأی) برای کالکشن فعال فعلی
  const [isSubmittingVote, setIsSubmittingVote] = useState(false);

  const handleVoteRequest = async () => {
    const collectionId = currentCollection?.collectionId;

    if (!subOneID) {
      showError("زیرنویس آنلاینی انتخاب نشده است");
      return;
    }
    if (!collectionId) {
      showError("برای این زیرنویس کالکشن فعالی وجود ندارد");
      return;
    }
    if (isSubmittingVote) return;

    setIsSubmittingVote(true);
    try {
      const result = await submitVoteRequest({
        collectionId,
        subtitleUploadId: subOneID,
      });
      if (result.alreadyExists) {
        showSuccess("برای این زیرنویس قبلاً محتوای یادگیری وجود دارد");
        await fetchUserLearning(subOneID);
      } else if (result.alreadyVoted) {
        showSuccess("درخواست شما قبلاً ثبت شده است");
      } else {
        showSuccess("درخواست استخراج لغات و اصطلاحات با موفقیت ثبت شد");
      }
    } catch (error) {
      console.error("vote request failed:", error);
      showError(
        error?.message || "خطا در ثبت درخواست. لطفاً بعداً تلاش کنید.",
      );
    } finally {
      setIsSubmittingVote(false);
    }
  };

  // اشتراک‌گذاری لینک رأی کالکشن
  const [showShareMenu, setShowShareMenu] = useState(false);
  const voteShareUrl = currentCollection?.collectionId
    ? `${window.location.origin}/vote/collections/${currentCollection.collectionId}`
    : "";

  const handleCopyShareLink = async (silent = false) => {
    if (!voteShareUrl) return false;
    try {
      await navigator.clipboard.writeText(voteShareUrl);
      if (!silent) showSuccess("لینک اشتراک‌گذاری کپی شد");
      return true;
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = voteShareUrl;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "-9999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        if (!silent) showSuccess("لینک اشتراک‌گذاری کپی شد");
        return true;
      } catch (e) {
        if (!silent) showError("کپی لینک ناموفق بود");
        return false;
      } finally {
        document.body.removeChild(textArea);
      }
    }
  };

  const handlePlayItem = (item) => {
    if (typeof item.timestamp !== "number") return;
    handleSeek(item.timestamp);
    if (videoRef.current) {
      videoRef.current.currentTime = item.timestamp;
      videoRef.current
        .play()
        .catch(() => console.warn("video play blocked"));
    }
  };
  const swappingRef = useRef(false);

  const setSubtitle1Track = (text) => {
    setSubtitle1Text(text);
    window.dispatchEvent(new CustomEvent("subtitle1change", { detail: text }));
  };

  // ذخیره زیرنویس اول فعلی (فقط در اولین انتخاب)
  const saveOriginalSubtitleIfNeeded = () => {
    if (savedOriginalSubtitle) return;
    const orig = getActiveSubtitle(1);
    if (!orig) {
      setSavedOriginalSubtitle({ sourceType: "none" });
      return;
    }
    const isOnline =
      orig.source === "online" ||
      orig.sourceType === "online" ||
      orig.subtitlesUploadId ||
      orig.onlineId;
    if (isOnline) {
      setSavedOriginalSubtitle({
        sourceType: "online",
        uploadId: orig.subtitlesUploadId || orig.onlineId,
        language: orig.language || "en",
        metadata: { ...orig },
      });
    } else {
      setSavedOriginalSubtitle({
        sourceType: "local",
        rawContent: orig.rawContent || "",
        language: orig.language || "unknown",
        metadata: { ...orig },
      });
    }
  };

  const handleSelectSubtitle = async (doc) => {
    if (!doc?.subtitleUploadId) return;
    if (swappingRef.current) return;
    swappingRef.current = true;
    saveOriginalSubtitleIfNeeded();
    setIsSwapping(true);
    try {
      const content = await fetchSubtitleContent(doc.subtitleUploadId);
      const lang = doc.subtitlesLanguage || "en";
      const file = new File([content], `subtitle1.${lang}.srt`, {
        type: "text/plain",
      });

      const metadata = {
        sourceType: "online",
        onlineId: doc.subtitleUploadId,
        subtitlesUploadId: doc.subtitleUploadId,
        language: lang,
        nameOfMovie: doc.movieTitle || doc.nameOfMovie || "",
        movieName: doc.movieTitle || doc.nameOfMovie || "",
        videoType: doc.movieType || doc.type || "movie",
        season: doc.season?.toString() || "",
        episode: doc.episode?.toString() || "",
        coverUrl: doc.coverUrl || "",
        productionYear: doc.productionYear || "",
        _source: "backend",
      };

      removeSubtitle(1);
      const success = await setupSubtitle(
        file,
        lang,
        1,
        setSubtitle1Track,
        metadata,
      );
      if (!success) {
        console.error("setupSubtitle failed for selected subtitle");
        return;
      }
      setSubtitle1Loaded(true);
      setSubtitle1Delay(0);
      setAutoLoadedSub1Id(doc.subtitleUploadId);

      // لغات و اصطلاحات زیرنویس جدید را دریافت کن
      await fetchUserLearning(doc.subtitleUploadId);

      setPickAnotherMode(false);
      setShowCompatPrompt(true);
    } catch (error) {
      console.error("Error selecting subtitle:", error);
    } finally {
      swappingRef.current = false;
      setIsSwapping(false);
    }
  };

  const handleAcceptCompat = () => {
    setShowCompatPrompt(false);
    setPickAnotherMode(false);
  };

  const handleRestoreOriginal = async () => {
    if (!savedOriginalSubtitle || savedOriginalSubtitle.sourceType === "none")
      return;
    if (swappingRef.current) return;
    swappingRef.current = true;
    setIsSwapping(true);
    try {
      const orig = savedOriginalSubtitle;
      if (orig.sourceType === "online") {
        const content = await fetchSubtitleContent(orig.uploadId);
        const file = new File([content], `subtitle1.${orig.language}.srt`, {
          type: "text/plain",
        });
        const success = await setupSubtitle(
          file,
          orig.language,
          1,
          setSubtitle1Track,
          orig.metadata,
        );
        if (!success) return;
        setSubtitle1Loaded(true);
        setAutoLoadedSub1Id(orig.uploadId);
      } else {
        const file = new File([orig.rawContent || ""], "subtitle1.srt", {
          type: "text/plain",
        });
        const success = await setupSubtitle(
          file,
          orig.language,
          1,
          setSubtitle1Track,
          orig.metadata,
        );
        if (!success) return;
        setSubtitle1Loaded(true);
        setAutoLoadedSub1Id(null);
      }
      setSubtitle1Delay(0);

      // دوباره حالت اولیه (خطای «تحلیل نشده») را بازیابی کن
      const origId =
        orig.sourceType === "online"
          ? orig.uploadId
          : orig.metadata?.subtitlesUploadId || null;
      await fetchUserLearning(origId);

      setShowCompatPrompt(false);
      setPickAnotherMode(false);
    } catch (error) {
      console.error("Error restoring subtitle:", error);
    } finally {
      swappingRef.current = false;
      setIsSwapping(false);
    }
  };

  const handlePickAnother = () => {
    setShowCompatPrompt(false);
    setPickAnotherMode(true);
  };

  // === فرم جستجوی فیلم/سریال با لغات و اصطلاحات استخراج‌شده ===
  const [searchFormTouched, setSearchFormTouched] = useState(false);
  const [searchName, setSearchName] = useState(videoMetadata?.filmName || "");
  const [searchType, setSearchType] = useState(
    videoMetadata?.videoType === "series" ? "series" : "movie",
  );
  const [searchSeason, setSearchSeason] = useState(
    videoMetadata?.season || "",
  );
  const [searchEpisode, setSearchEpisode] = useState(
    videoMetadata?.episode || "",
  );
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  // searchResults از context می‌آید تا با تغییر تب یا باز/بسته‌شدن سایدبار از بین نرود

  // پر کردن پیش‌فرض فرم از داده‌های موجود؛ تا وقتی کاربر فرم را دست‌کاری نکرده
  // باشد با تغییر ویدیو هم مقادیر دوباره به‌روزرسانی می‌شوند.
  useEffect(() => {
    if (searchFormTouched) return;
    setSearchName(videoMetadata?.filmName || "");
    setSearchType(videoMetadata?.videoType === "series" ? "series" : "movie");
    setSearchSeason(videoMetadata?.season || "");
    setSearchEpisode(videoMetadata?.episode || "");
  }, [videoMetadata, searchFormTouched]);

  const markSearchTouched = () => setSearchFormTouched(true);

  const handleSearch = async (event) => {
    event.preventDefault();
    setIsSearching(true);
    setSearchError("");
    setSearchResults(null);
    try {
      const result = await searchCompletedSubtitles({
        nameOfMovie: searchName.trim(),
        movieType: searchType,
        season: searchType === "series" ? searchSeason : "",
        episode: searchType === "series" ? searchEpisode : "",
      });
      setSearchResults(result);
    } catch (error) {
      console.error("learning search failed:", error);
      setSearchError(
        error?.message || "خطا در جستجو. لطفاً بعداً تلاش کنید.",
      );
    } finally {
      setIsSearching(false);
    }
  };

  const showSearchHelper = showLearningError;
  const formatResultTitle = (doc) =>
    doc?.movieTitle || doc?.nameOfMovie || "بدون نام";
  const formatResultMeta = (doc) => {
    const isSeries =
      doc?.movieType === "series" ||
      doc?.movieType === "serial" ||
      doc?.type === "series" ||
      doc?.type === "serial";
    const parts = [];
    if (isSeries) {
      parts.push("سریال");
      if (doc?.season) parts.push(`فصل ${doc.season}`);
      if (doc?.episode) parts.push(`قسمت ${doc.episode}`);
    } else {
      parts.push("فیلم");
    }
    if (doc?.subtitlesLanguage) parts.push(doc.subtitlesLanguage);
    return parts.join(" • ");
  };

  // در حال بارگذاری زیرنویس/لغات، نشانگر بچرخد
  const showLoading = isSwapping || (isLearningLoading && !hasCachedData);
  const showPickList = pickAnotherMode;

  if (showLoading) {
    return (
      <div
        className="words-list"
        style={{ "--sidebar-font-scale": String(fontScale) }}
      >
        <div className="tab-placeholder">
          <i className="fas fa-spinner fa-spin"></i>
          <p>در حال بارگذاری زیرنویس و لغات...</p>
        </div>
      </div>
    );
  }

  if (!hasCachedData && !showPickList) {
    return (
      <div className="words-empty">
        <div className="tab-placeholder">
          <i className="fas fa-book-open"></i>
          {showSearchHelper ? (
            <>
              <button
                className="words-check-btn"
                onClick={handleVoteRequest}
                disabled={isSubmittingVote || !currentCollection?.collectionId}
                title={
                  currentCollection?.collectionId
                    ? `collectionId: ${currentCollection.collectionId}`
                    : "برای این زیرنویس کالکشن فعالی وجود ندارد"
                }
              >
                {isSubmittingVote ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <i className="fas fa-paper-plane"></i>
                )}
                <span>درخواست استخراج لغات و اصطلاحات</span>
              </button>
              {!currentCollection?.collectionId && (
                <div className="words-vote-disabled-hint">
                  <p>
                    استخراج لغات و اصطلاحات فقط در فیلم‌هایی دارای دو زیرنویس
                    همزمان امکان‌پذیر است
                  </p>
                  <a
                    className="words-more-info-btn"
                    href={`${window.location.origin}/vote/FAQs#collections`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fas fa-info-circle"></i>
                    توضیحات بیشتر
                  </a>
                </div>
              )}

              {currentCollection?.collectionId && (
                <>
                  <div className="words-vote-actions">
                    <button
                      className="words-small-action-btn"
                      onClick={() =>
                        window.open(
                          voteShareUrl,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                      title="درخواست‌های من"
                    >
                      <i className="fas fa-list-check"></i>
                    </button>
                    <button
                      className="words-small-action-btn"
                      onClick={() => setShowShareMenu((prev) => !prev)}
                      title="اشتراک‌گذاری لینک رأی کالکشن"
                    >
                      <i className="fas fa-share-alt"></i>
                    </button>
                  </div>

                  {showShareMenu && (
                    <div className="words-share-row">
                      <button
                        className="words-share-btn"
                        onClick={handleCopyShareLink}
                        title="کپی لینک"
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                      <a
                        className="words-share-btn"
                        href={`https://t.me/share/url?url=${encodeURIComponent(voteShareUrl)}&text=${encodeURIComponent(voteShareUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="تلگرام"
                      >
                        <i className="fab fa-telegram"></i>
                      </a>
                      <a
                        className="words-share-btn"
                        href={`https://www.instagram.com/?url=${encodeURIComponent(voteShareUrl)}`}
                        onClick={() => handleCopyShareLink(true)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="اینستاگرام"
                      >
                        <i className="fab fa-instagram"></i>
                      </a>
                      <a
                        className="words-share-btn"
                        href={`https://api.whatsapp.com/send?text=${encodeURIComponent(voteShareUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="واتس‌آپ"
                      >
                        <i className="fab fa-whatsapp"></i>
                      </a>
                      <a
                        className="words-share-btn"
                        href={`https://ble.ir/?url=${encodeURIComponent(voteShareUrl)}`}
                        onClick={() => handleCopyShareLink(true)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="بله"
                      >
                        <span className="words-share-letters words-share-bale">ب</span>
                      </a>
                      <a
                        className="words-share-btn"
                        href={`https://rubika.ir/?url=${encodeURIComponent(voteShareUrl)}`}
                        onClick={() => handleCopyShareLink(true)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="روبیکا"
                      >
                        <span className="words-share-letters words-share-rubika">ر</span>
                      </a>
                      <a
                        className="words-share-btn"
                        href={`https://eitaa.com/?url=${encodeURIComponent(voteShareUrl)}`}
                        onClick={() => handleCopyShareLink(true)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="ایتا"
                      >
                        <span className="words-share-letters words-share-eitaa">ا</span>
                      </a>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <p className="words-empty-hint">
                برای مشاهده لغات و اصطلاحات این فیلم/سریال، روی دکمه
                «بررسی لغات و اصطلاحات» کلیک کنید.
              </p>
              <button
                className="words-check-btn"
                onClick={handleCheckLearning}
                disabled={isLearningLoading || !subOneID}
                title={subOneID ? `subOneID: ${subOneID}` : "زیرنویس آنلاینی انتخاب نشده است"}
              >
                {isLearningLoading ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <i className="fas fa-magnifying-glass"></i>
                )}
                <span>بررسی لغات و اصطلاحات</span>
              </button>
            </>
          )}
        </div>

        {showSearchHelper && (
          <div className="words-search-section">
            <div className="words-search-title">
              <i className="fas fa-search"></i>
              <span>جستجوی فیلم/سریال دارای لغات استخراج‌شده</span>
            </div>
            <form className="words-search-form" onSubmit={handleSearch}>
              <div className="words-search-field">
                <label htmlFor="words-search-name">نام فیلم/سریال</label>
                <input
                  id="words-search-name"
                  type="text"
                  value={searchName}
                  onChange={(e) => {
                    markSearchTouched();
                    setSearchName(e.target.value);
                  }}
                  placeholder="مثال: Friends"
                  dir="auto"
                />
              </div>

              <div className="words-search-field">
                <label htmlFor="words-search-type">نوع فیلم/سریال</label>
                <select
                  id="words-search-type"
                  value={searchType}
                  onChange={(e) => {
                    markSearchTouched();
                    setSearchType(e.target.value);
                    setSearchResults(null);
                  }}
                >
                  <option value="movie">فیلم</option>
                  <option value="series">سریال</option>
                </select>
              </div>

              {searchType === "series" && (
                <div className="words-search-row">
                  <div className="words-search-field">
                    <label htmlFor="words-search-season">شماره فصل</label>
                    <input
                      id="words-search-season"
                      type="text"
                      inputMode="numeric"
                      value={searchSeason}
                      onChange={(e) => {
                        markSearchTouched();
                        setSearchSeason(e.target.value);
                      }}
                      placeholder="مثلاً ۱"
                    />
                  </div>
                  <div className="words-search-field">
                    <label htmlFor="words-search-episode">شماره قسمت</label>
                    <input
                      id="words-search-episode"
                      type="text"
                      inputMode="numeric"
                      value={searchEpisode}
                      onChange={(e) => {
                        markSearchTouched();
                        setSearchEpisode(e.target.value);
                      }}
                      placeholder="مثلاً ۱"
                    />
                  </div>
                </div>
              )}

              <button
                className="words-search-btn"
                type="submit"
                disabled={isSearching}
              >
                {isSearching ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <i className="fas fa-search"></i>
                )}
                جستجو
              </button>
            </form>

            <p className="words-search-note">
              <i className="fas fa-info-circle"></i>
              برای انجام جستجو، حتماً دکمه «جستجو» را بزنید؛ حتی اگر فیلدها پر
              شده باشند، تا وقتی روی «جستجو» کلیک نشود نتیجه‌ای نمایش داده
              نمی‌شود.
            </p>

            {searchError && (
              <p className="words-search-error">
                <i className="fas fa-exclamation-circle"></i>
                {searchError}
              </p>
            )}

            {searchResults && (
              <div className="words-search-results">
                {!searchResults.totalDocs ||
                searchResults.docs?.length === 0 ? (
                  <p className="words-search-empty">
                    زیرنویسی با این مشخصات یافت نشد که لغات و اصطلاحات آن از قبل
                    استخراج شده باشد. می‌توانید نام یا اطلاعات دیگری را امتحان
                    کنید.
                  </p>
                ) : (
                  <>
                    <p className="words-search-count">
                      {searchResults.totalDocs} زیرنویس با لغات و اصطلاحات
                      استخراج‌شده یافت شد
                    </p>
                    <div className="words-search-result-list">
                      {searchResults.docs.map((doc) => (
                        <div
                          className="words-search-result"
                          key={doc?._id || doc?.subtitleUploadId}
                        >
                          <span className="words-search-result-title">
                            {formatResultTitle(doc)}
                          </span>
                          <span className="words-search-result-meta">
                            {formatResultMeta(doc)}
                            {doc?.productionYear
                              ? ` • ${doc.productionYear}`
                              : ""}
                          </span>
                          <span
                            className="words-search-result-id"
                            dir="ltr"
                          >
                            {doc?.subtitleUploadId}
                          </span>
                          <button
                            className="words-select-btn"
                            onClick={() => handleSelectSubtitle(doc)}
                            disabled={isSwapping}
                          >
                            <i className="fas fa-check"></i>
                            <span>انتخاب و نمایش اصطلاحات</span>
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="words-search-result-hint">
                      با انتخاب هر یک از گزینه‌ها، علاوه بر دریافت لیست لغات و
                      اصطلاحات، زیرنویس اول فیلم تغییر می‌کند و زیرنویس انتخاب‌شده
                      جایگزین آن می‌گردد.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="words-list"
      style={{ "--sidebar-font-scale": String(fontScale) }}
    >
      {showCompatPrompt && (
        <div className="words-compat-prompt">
          <p className="words-compat-question">آیا زیرنویس سازگار است؟</p>
          <div className="words-compat-actions">
            <button className="words-compat-btn words-compat-yes" onClick={handleAcceptCompat}>
              بله
            </button>
            <button className="words-compat-btn words-compat-back" onClick={handleRestoreOriginal}>
              برگشت به اول
            </button>
            <button className="words-compat-btn words-compat-another" onClick={handlePickAnother}>
              انتخاب دیگر
            </button>
          </div>
        </div>
      )}

      {showPickList ? (
        <div className="words-pick-list">
          <button
            className="words-search-back-btn"
            onClick={handleRestoreOriginal}
            disabled={isSwapping}
          >
            <i className="fas fa-arrow-right"></i>
            <span>بازگشت به زیرنویس قبلی</span>
          </button>
          {searchResults?.docs?.length > 0 ? (
            <>
              <p className="words-search-count">
                {searchResults.totalDocs} زیرنویس با لغات و اصطلاحات
                استخراج‌شده یافت شد
              </p>
              <div className="words-search-result-list">
                {searchResults.docs.map((doc) => (
                  <div
                    className="words-search-result"
                    key={doc?._id || doc?.subtitleUploadId}
                  >
                    <span className="words-search-result-title">
                      {formatResultTitle(doc)}
                    </span>
                    <span className="words-search-result-meta">
                      {formatResultMeta(doc)}
                      {doc?.productionYear
                        ? ` • ${doc.productionYear}`
                        : ""}
                    </span>
                    <span className="words-search-result-id" dir="ltr">
                      {doc?.subtitleUploadId}
                    </span>
                    <button
                      className="words-select-btn"
                      onClick={() => handleSelectSubtitle(doc)}
                      disabled={isSwapping}
                    >
                      <i className="fas fa-check"></i>
                      <span>انتخاب و نمایش اصطلاحات</span>
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="tab-placeholder">
              <i className="fas fa-search"></i>
              <p>
                برای انتخاب زیرنویس دیگری، ابتدا از فرم جستجو استفاده کنید.
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          {sortedItems.length === 0 ? (
            <div className="tab-placeholder">
              <i className="fas fa-book-open"></i>
              <p>لغت یا اصطلاحی یافت نشد</p>
            </div>
          ) : (
            sortedItems.map((item, index) => (
              <LearningItemRow
                key={`${item.subtitleIndex}-${index}`}
                item={item}
                onPlay={handlePlayItem}
                onOpenDetail={onOpenDetail}
              />
            ))
          )}
        </>
      )}
    </div>
  );
};

const Sidebar = ({
  children,
  onCollapse,
  subtitle1Data,
  currentTime,
  onSeekTo,
}) => {
  const [activeTab, setActiveTab] = useState("subtitles");
  const [showSettings, setShowSettings] = useState(false);

  // آیتم لغت/اصطلاح انتخابی برای باکس توضیحات (مشترک بین هر دو تب)
  const [detailItem, setDetailItem] = useState(null);
  const { pauseVideo, isIOS, isFullscreen } = useVideo();

  const handleOpenDetail = (item) => {
    pauseVideo();
    setDetailItem(item);
  };

  const handleCloseDetail = () => setDetailItem(null);

  // نمایش فقط اصطلاحاتی که توضیحات کامل دارند (پیش‌فرض: فعال)
  const [showCompleteOnly, setShowCompleteOnly] = useState(() => {
    const saved = safeGetItem("sidebarShowCompleteOnly");
    return saved !== null ? saved === "true" : true;
  });

  useEffect(() => {
    safeSetItem("sidebarShowCompleteOnly", String(showCompleteOnly));
  }, [showCompleteOnly]);

  // نمایش اصطلاحات روی ویدیو بدون نیاز به سایدبار (پیش‌فرض: فعال)
  const [showPhrasesNoSidebar, setShowPhrasesNoSidebar] = useState(() => {
    const saved = safeGetItem("sidebarShowPhrasesNoSidebar");
    return saved !== null ? saved === "true" : true;
  });

  useEffect(() => {
    safeSetItem("sidebarShowPhrasesNoSidebar", String(showPhrasesNoSidebar));
  }, [showPhrasesNoSidebar]);

  // مقیاس فونت سایدبار بر حسب درصد (۵۰٪ تا ۳۰۰٪) - برای هر دو تب
  const [fontScale, setFontScale] = useState(() => {
    const saved = safeGetItem("sidebarFontScale");
    const parsed = saved ? parseInt(saved, 10) : 100;
    return Number.isFinite(parsed)
      ? Math.min(300, Math.max(50, parsed))
      : 100;
  });

  useEffect(() => {
    safeSetItem("sidebarFontScale", String(fontScale));
  }, [fontScale]);

  const fontScaleValue = fontScale / 100;

  // مقیاس فونت باکس توضیحات (۵۰٪ تا ۳۰۰٪) - با پیش‌فرض کمی بزرگ‌تر از حالت عادی
  const [detailFontScale, setDetailFontScale] = useState(() => {
    const saved = safeGetItem("sidebarDetailFontScale");
    const parsed = saved ? parseInt(saved, 10) : 100;
    return Number.isFinite(parsed)
      ? Math.min(300, Math.max(50, parsed))
      : 100;
  });

  useEffect(() => {
    safeSetItem("sidebarDetailFontScale", String(detailFontScale));
  }, [detailFontScale]);

  const detailFontScaleValue = detailFontScale / 100;

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <i className="fas fa-graduation-cap"></i>
          <span className="sidebar-title">پنل یادگیری</span>
        </div>

        <button
          className={`sidebar-icon-btn ${showSettings ? "active" : ""}`}
          onClick={() => setShowSettings((prev) => !prev)}
          title="تنظیمات سایدبار"
        >
          <i className="fas fa-cog"></i>
        </button>

        {onCollapse && (
          <button
            className="sidebar-icon-btn"
            onClick={onCollapse}
            title="جمع کردن پنل"
          >
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      {showSettings ? (
        <div className="sidebar-settings">
          <div className="sidebar-settings-header">
            <i className="fas fa-sliders-h"></i>
            <span className="sidebar-settings-title">تنظیمات</span>
          </div>

          <div className="sidebar-settings-body">
            <div className="settings-group">
              <div className="settings-group-title">
                <i className="fas fa-columns"></i>
                <span>سایدبار</span>
              </div>

              <div className="settings-row">
                <span className="settings-row-label">
                  <i className="fas fa-text-height"></i>
                  اندازه فونت سایدبار
                </span>
                <div className="font-scale-control">
                  <button
                    className="font-scale-btn"
                    onClick={() =>
                      setFontScale((s) => Math.max(50, s - 5))
                    }
                    title="کوچک‌تر"
                  >
                    <i className="fas fa-minus"></i>
                  </button>
                  <span className="font-scale-value">{fontScale}٪</span>
                  <button
                    className="font-scale-btn"
                    onClick={() =>
                      setFontScale((s) => Math.min(300, s + 5))
                    }
                    title="بزرگ‌تر"
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                </div>
              </div>

              <div className="settings-row">
                <span className="settings-row-label">
                  <i className="fas fa-book-open"></i>
                  اندازه فونت باکس توضیحات
                </span>
                <div className="font-scale-control">
                  <button
                    className="font-scale-btn"
                    onClick={() =>
                      setDetailFontScale((s) => Math.max(50, s - 5))
                    }
                    title="کوچک‌تر"
                  >
                    <i className="fas fa-minus"></i>
                  </button>
                  <span className="font-scale-value">
                    {detailFontScale}٪
                  </span>
                  <button
                    className="font-scale-btn"
                    onClick={() =>
                      setDetailFontScale((s) => Math.min(300, s + 5))
                    }
                    title="بزرگ‌تر"
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                </div>
              </div>

              <div className="settings-row">
                <span className="settings-row-label">
                  <i className="fas fa-comment-dots"></i>
                  نمایش اصطلاحات بدون سایدبار
                </span>
                <label className="sidebar-toggle-switch">
                  <input
                    type="checkbox"
                    checked={showPhrasesNoSidebar}
                    onChange={(event) =>
                      setShowPhrasesNoSidebar(event.target.checked)
                    }
                    title="نمایش اصطلاحات روی ویدیو وقتی سایدبار جمع شده است"
                  />
                  <span className="sidebar-toggle-slider"></span>
                </label>
              </div>

              <div className="settings-row">
                <span className="settings-row-label">
                  <i className="fas fa-book-open"></i>
                  فقط لغات و اصطلاحات با توضیحات کامل
                </span>
                <label className="sidebar-toggle-switch">
                  <input
                    type="checkbox"
                    checked={showCompleteOnly}
                    onChange={(event) =>
                      setShowCompleteOnly(event.target.checked)
                    }
                    title="فقط اصطلاحاتی که توضیحات کامل دارند نمایش داده شوند"
                  />
                  <span className="sidebar-toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="sidebar-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === "subtitles"}
              className={`sidebar-tab ${
                activeTab === "subtitles" ? "active" : ""
              }`}
              onClick={() => setActiveTab("subtitles")}
            >
              <i className="fas fa-closed-captioning"></i>
              <span>زیرنویس</span>
            </button>

            <button
              role="tab"
              aria-selected={activeTab === "words"}
              className={`sidebar-tab ${
                activeTab === "words" ? "active" : ""
              }`}
              onClick={() => setActiveTab("words")}
            >
              <i className="fas fa-book-open"></i>
              <span>لغات و اصطلاحات</span>
            </button>
          </div>

          {activeTab === "words" && <WordsBetaBadge />}

          <div className="sidebar-content">
            {children ?? (
              activeTab === "subtitles" ? (
                <SubtitleTab
                  cues={subtitle1Data}
                  currentTime={currentTime}
                  onSeek={onSeekTo}
                  fontScale={fontScaleValue}
                  onOpenDetail={handleOpenDetail}
                />
              ) : (
                <WordsTab
                  fontScale={fontScaleValue}
                  showCompleteOnly={showCompleteOnly}
                  onOpenDetail={handleOpenDetail}
                />
              )
            )}
          </div>
        </>
      )}

      {detailItem &&
        createPortal(
          <WordsDetailBox
            item={detailItem}
            onClose={handleCloseDetail}
            fontScale={detailFontScaleValue}
          />,
          getPortalContainer(isIOS, isFullscreen)
        )}
    </aside>
  );
};

export default Sidebar;